import React, { useCallback, useMemo } from 'react';
import AppLayout from '../../../../../component/appLayout';
import {
  AIAgentsDetailsSection,
  BottomSection,
  Box,
  Description,
  DetailsWrapper,
  Heading,
  HeadingTitle,
  UserDetails,
  UserSubDetails
} from '../styles';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../../../../../component/button';
import BottomSubNavigation from '../../../../../component/bottomNavigation/subNavigation';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../../../../global/useMobile';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../../global/constants';
import ResponsiveHeader from '../../../../../component/responsiveHeader';
import { Container, Group, Header } from '../../../styles';
import IconButton from '../../../../../component/button/iconButton';
import Image from '../../../../../component/image';

const AiAgentDetails = () => {
  // Mobx store
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore;

  //Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();
  const params: { id: string } = useParams();

  const currentAgent = useMemo(() => {
    return currentWorkspace?.aiAgents?.find((agent) => agent.id === params.id);
  }, [currentWorkspace, params.id]);

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <Header>
                <Heading>
                  <IconButton
                    iconName='new-large-back-arrow-icon'
                    size={ICON_BUTTON_SIZE.SMALL}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={handleBack}
                  />
                  <HeadingTitle>AI Agents</HeadingTitle>
                </Heading>
              </Header>
            ) : (
              <ResponsiveHeader
                title={currentAgent?.name || ''}
                subTitle='AI Agents'
                image={{
                  url: currentAgent?.profile_photo || '',
                  title: currentAgent?.name || 'AI Agent'
                }}
              />
            )}
            <BottomSection>
              <UserDetails>
                <UserSubDetails>
                  {!isMobile && (
                    <Image
                      src={currentAgent?.profile_photo}
                      alt='ai-agent'
                      width={42}
                      height={42}
                      borderRadius='var(--border-radius-8)'
                    />
                  )}
                  <DetailsWrapper>
                    {!isMobile && <h1>{currentAgent?.name}</h1>}
                    <Description>{currentAgent?.description}</Description>
                  </DetailsWrapper>
                </UserSubDetails>
                <Button
                  title={currentAgent?.isActive ? 'Deactivate' : 'Activate'}
                  buttonType='button'
                  variant={currentAgent?.isActive ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
                />
              </UserDetails>
              <AIAgentsDetailsSection>
                <Box>
                  <h2>About Agent</h2>
                  <p>
                    Meet your on-demand SEO Expert Agent: a data-driven specialist equipped with the latest algorithm
                    insights and content-optimization strategies. Whether you’re launching a new landing page or
                    auditing existing content, this agent ensures your site climbs the rankings.
                  </p>
                </Box>
                <Box>
                  <h2>Core Expertise</h2>
                  <ul>
                    <li>
                      Keyword Research & Clustering: Identifies high-value, low-competition keywords and groups them
                      into thematic clusters.
                    </li>
                    <li>
                      On-Page Optimization: Recommends meta tags, header hierarchy, internal linking structures, and
                      content improvements for maximum relevance.
                    </li>
                    <li>
                      Technical SEO Audits: Scans for crawlability issues, broken links, mobile-friendliness, and
                      page-speed optimizations.
                    </li>
                    <li>
                      Content Gap Analysis: Suggests topics and subtopics to fill gaps in your content library and
                      outrank competitors.
                    </li>
                  </ul>
                </Box>
                <Box>
                  <h2>Use Cases</h2>
                  <h3>New Blog Post Optimization</h3>
                  <ul>
                    <li>Ask: “@SEOExpert, optimize this draft on ‘Remote Team Productivity Tips’ for Google.”</li>
                    <li>Result: A list of target keywords, suggested headings, and meta description draft.</li>
                  </ul>
                  <h3>Site Audit Summary</h3>
                  <ul>
                    <li>Ask: “@SEOExpert, audit our homepage and highlight any critical technical issues.”</li>
                    <li>
                      Result: A prioritized list of fixes—missing alt tags, slow-loading scripts, and mobile rendering
                      adjustments.
                    </li>
                  </ul>
                  <h3>Competitive Gap Analysis</h3>
                  <ul>
                    <li>
                      Ask: “@SEOExpert, compare our Services page to AcmeCorp’s and identify 3 topic areas we’re
                      missing.”
                    </li>
                    <li>
                      Result: A report outlining uncovered subtopics, suggested new sections, and sample blog titles.
                    </li>
                  </ul>
                </Box>
              </AIAgentsDetailsSection>
            </BottomSection>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(AiAgentDetails);
